import { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { useTheme } from 'next-themes';
import { SEVERITY_COLORS } from './distributionUtils';

export interface BubbleChartDataPoint {
  name: string;
  x: number;
  y: number;
  size: number;
  description?: string;
  additionalInfo?: string;
}

export interface BubbleChartAxis {
  name: string;
  min?: number;
  max?: number;
  nameGap?: number;
}

interface ReusableBubbleChartProps {
  data: BubbleChartDataPoint[];
  xAxis: BubbleChartAxis;
  yAxis: BubbleChartAxis;
  height?: number;
  className?: string;
  title?: string;
  emptyStateIcon?: React.ComponentType<{ className?: string }>;
  emptyStateMessage?: string;
  colorFunction?: (dataPoint: BubbleChartDataPoint, index: number) => string;
  sizeMultiplier?: number;
  tooltipFormatter?: (dataPoint: BubbleChartDataPoint) => string;
  seriesName?: string;
}

export const ReusableBubbleChart: React.FC<ReusableBubbleChartProps> = ({
  data,
  xAxis,
  yAxis,
  height = 320, 
  className = '',
  title,
  emptyStateIcon: EmptyStateIcon,
  emptyStateMessage = "No data available",
  colorFunction,
  sizeMultiplier = 1,
  tooltipFormatter,
  seriesName = 'Bubble Chart'
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const { theme } = useTheme();

  useEffect(() => {
    if (!chartRef.current) return;

    if (chartInstance.current) {
      chartInstance.current.dispose();
    }

    chartInstance.current = echarts.init(chartRef.current, theme === 'dark' ? 'dark' : '');

    const hasData = data && data.length > 0;
    const hasValidData = hasData && data.some(point => 
      (point.x > 0 || point.y > 0) && point.size > 0
    );

    
    if (!hasValidData) {
      const emptyOption = {
        backgroundColor: 'transparent',
        title: {
          text: emptyStateMessage,
          left: 'center',
          top: 'middle',
          textStyle: {
            color: 'hsl(var(--muted-foreground))',
            fontSize: 14,
            fontWeight: 'normal'
          }
        },
        grid: {
          left: '10%',
          right: '10%',
          top: '10%',
          bottom: '10%'
        },
        xAxis: {
          show: false,
          type: 'value'
        },
        yAxis: {
          show: false,
          type: 'value'
        }
      };
      chartInstance.current.setOption(emptyOption);
      return;
    }

    const chartData = data.map((point, index) => [
      point.x,
      point.y,
      point.size,
      point.name,
      point.description || '',
      point.additionalInfo || ''
    ]);

    const defaultColorFunction = (dataPoint: BubbleChartDataPoint) => {
      const score = dataPoint.y; 
      return score >= 90 ? SEVERITY_COLORS.critical :
        score >= 70 ? SEVERITY_COLORS.high :
          score >= 40 ? SEVERITY_COLORS.medium : SEVERITY_COLORS.low;
    };

    const defaultTooltipFormatter = (param: any) => {
      const data = param.data;
      return `
        <div class="font-medium">${data[3]}</div>
        <div class="text-sm">${data[4]}</div>
        <div class="font-medium mt-1">${xAxis.name}: ${data[0].toFixed(2)}</div>
        <div class="font-medium">${yAxis.name}: ${data[1].toFixed(2)}</div>
        <div>Size: ${Math.round(data[2])}</div>
      `;
    };

    const option = {
      backgroundColor: 'transparent',
      title: title ? {
        text: title,
        left: 'center',
        top: '5%',
        textStyle: {
          fontSize: 14,
          fontWeight: 'normal'
        }
      } : undefined,
      grid: {
        left: '10%',
        right: '5%',
        top: title ? '15%' : '10%',
        bottom: '15%'
      },
      tooltip: {
        formatter: tooltipFormatter || defaultTooltipFormatter
      },
      xAxis: {
        name: xAxis.name,
        nameLocation: 'middle',
        nameGap: xAxis.nameGap || 30,
        min: xAxis.min,
        max: xAxis.max,
        splitLine: {
          lineStyle: {
            type: 'dashed'
          }
        }
      },
      yAxis: {
        name: yAxis.name,
        nameLocation: 'middle',
        nameGap: yAxis.nameGap || 40,
        min: yAxis.min,
        max: yAxis.max,
        splitLine: {
          lineStyle: {
            type: 'dashed'
          }
        },
        scale: true
      },
      series: [{
        name: seriesName,
        data: chartData,
        type: 'scatter',
        symbolSize: function (data: any) {
          return Math.round(data[2]) * sizeMultiplier;
        },
        emphasis: {
          focus: 'self',
          label: {
            show: true,
            formatter: function (param: any) {
              return param.data[3];
            },
            position: 'top'
          }
        },
        itemStyle: {
          color: function (params: any) {
            if (colorFunction) {
              const dataPoint: BubbleChartDataPoint = {
                name: params.data[3],
                x: params.data[0],
                y: params.data[1],
                size: params.data[2],
                description: params.data[4],
                additionalInfo: params.data[5]
              };
              return colorFunction(dataPoint, params.dataIndex);
            }
            return defaultColorFunction({
              name: params.data[3],
              x: params.data[0],
              y: params.data[1],
              size: params.data[2]
            });
          },
          opacity: 0.8
        }
      }]
    };

    chartInstance.current.setOption(option);

    setTimeout(() => {
      chartInstance.current?.resize();
    }, 100);

    const handleResize = () => {
      chartInstance.current?.resize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, [data, xAxis, yAxis, title, theme, colorFunction, sizeMultiplier, tooltipFormatter, seriesName]);

  const hasData = data && data.length > 0;

  if (!hasData) {
    return (
      <div className={`flex items-center justify-center text-gray-500 ${className}`} style={{ height }}>
        <div className="text-center">
          {EmptyStateIcon && <EmptyStateIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />}
          <p>{emptyStateMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={chartRef} 
      className={`h-120 w-full ${className}`}
    
    />
  );
};

export default ReusableBubbleChart;